import { useEffect, useState } from "react";

interface UseFetchOptions<TPayload, TResponse> {
  defaultData?: TResponse | null;
  fetcher: (payload: TPayload) => Promise<TResponse>;
  stopRequest?: boolean;
  immediately?: boolean;
  onSuccess?: (response: TResponse) => void;
  onError?: (error: any) => void;
}

interface UseFetchState<TResponse> {
  loading: boolean;
  error: any;
  data: TResponse | null;
}

export const useFetch = <TPayload, TResponse>(
  {
    defaultData = null,
    fetcher,
    stopRequest = false,
    immediately = false,
    onSuccess,
    onError,
  }: UseFetchOptions<TPayload, TResponse>,
  payload: TPayload,
  triggers: any[] = []
) => {
  const initialState: UseFetchState<TResponse> = {
    loading: immediately,
    error: null,
    data: defaultData,
  };

  const [{ loading, error, data }, setState] =
    useState<UseFetchState<TResponse>>(initialState);

  const fetchData = () => {
    if (stopRequest) {
      return;
    }

    if (!loading) {
      setState((state) => ({
        ...state,
        loading: true,
      }));
    }

    fetcher(payload)
      .then((response) => {
        setState({
          loading: false,
          error: null,
          data: response,
        });

        if (onSuccess) {
          onSuccess(response);
        }
      })
      .catch((err) => {
        setState((state) => ({
          ...state,
          loading: false,
          error: err,
        }));

        if (onError) {
          onError(err);
        }
      });
  };

  useEffect(fetchData, [stopRequest, ...triggers]);

  return { loading, error, data, setState, refetch: fetchData };
};
